import React, { useState, ChangeEvent } from 'react';
import axios from 'axios';
import { authenticatedPost } from '../utils/api';
import { Button, Box, Typography, CircularProgress } from '@mui/material';
import { useBook } from '../Context/BookContext';
import { useAuth } from '../Context/AuthContext';

const getPresignedUrlEndpoint = process.env.REACT_APP_LAMBDA_GET_PRESIGNED_URL_UPLOAD_BOOK;
const processUploadedBookEndpoint = process.env.REACT_APP_LAMBDA_PROCESS_UPLOADED_BOOK;

interface PresignedUrlResponse {
  presigned_url: string;
  file_id: string;
  s3_key: string;
  user_id: string;
  file_name: string;
}

interface LastUploadedBookData {
  FileId: string;
  FileName: string;
  uploadTime: string;
}

const BookUploader: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const { refreshTrigger, setRefreshTrigger, setLastUploadedBook } = useBook();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleFileSelect = (file: File | null): void => {
    if (file && file.name.endsWith('.epub')) {
      setSelectedFile(file);
      setUploadStatus('');
    } else if (file) {
      setUploadStatus('Error: Please select an EPUB file.');
      setSelectedFile(null);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!isAuthenticated || !user?.userId) {
      setUploadStatus('Error: User not authenticated.');
      return;
    }

    if (!selectedFile) {
      setUploadStatus('No file selected.');
      return;
    }

    if (!getPresignedUrlEndpoint || !processUploadedBookEndpoint) {
      setUploadStatus('Error: Upload endpoints not configured.');
      return;
    }

    setIsLoading(true);
    setUploadStatus('Preparing upload...');

    try {
      // Step 1: Get presigned URL from Lambda
      const presignedUrlResponse = await authenticatedPost<PresignedUrlResponse>(getPresignedUrlEndpoint, {
        file_name: selectedFile.name,
      });

      const { 
        presigned_url, 
        file_id, 
        s3_key,
        file_name
      } = presignedUrlResponse.data;

      setUploadStatus('Uploading file to S3...');

      // Step 2: Upload the file directly to S3 using the presigned URL
      try {
        await axios.put(presigned_url, selectedFile, {
          headers: {
            'Content-Type': 'application/epub+zip',
          },
        });
      } catch (uploadError) {
        console.error('S3 Upload Error:', uploadError);
        throw new Error(`S3 upload failed: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
      }

      setUploadStatus('File uploaded to S3, processing metadata...');

      // Step 3: Process the uploaded file metadata
      // ProcessUploadedBook will automatically trigger GetBookInformation Lambda
      const processResponse = await authenticatedPost(processUploadedBookEndpoint, {
        s3_key,
        file_id,
        file_name,
      });

      console.log('File processed:', processResponse.data);
      setUploadStatus('File uploaded successfully! Book analysis has been initiated and will be available shortly.');
      
      // Track the last uploaded book to monitor its processing
      const lastUploadedData: LastUploadedBookData = {
        FileId: file_id, 
        FileName: file_name,
        uploadTime: new Date().toISOString()
      };
      setLastUploadedBook(lastUploadedData);
      
      // Trigger book list refresh
      setRefreshTrigger(refreshTrigger + 1);
    } catch (error) {
      console.error('Error during upload process:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during upload';
      setUploadStatus(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ gap: 2 }}
    >
      {/* Drag and Drop Zone */}
      <Box
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          minHeight: 200,
          border: '3px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 3,
          backgroundColor: isDragActive ? 'action.hover' : 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          p: 4,
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          }
        }}
      >
        <Typography variant="h6" sx={{ color: 'text.primary', textAlign: 'center' }}>
          {isDragActive ? 'Drop your EPUB file here' : 'Drag & Drop your EPUB file here'}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          or
        </Typography>

        <Button
          variant="contained"
          component="label"
          color="primary"
          disabled={isLoading}
          sx={{ textTransform: 'none' }}
        >
          Select EPUB File
          <input
            type="file"
            accept=".epub"
            hidden
            onChange={handleFileChange}
          />
        </Button>

        {selectedFile && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: 'success.light', 
            borderRadius: 2,
            width: '100%',
            textAlign: 'center'
          }}>
            <Typography variant="body1" sx={{ color: 'success.dark', fontWeight: 600 }}>
              ✓ Selected File: {selectedFile.name}
            </Typography>
          </Box>
        )}
      </Box>

      <Button
        variant="contained"
        color="warning"
        onClick={handleUpload}
        disabled={!selectedFile || isLoading}
        sx={{ textTransform: 'none', fontWeight: 600, py: 1.5, px: 4 }}
      >
        {isLoading ? 'Uploading...' : 'Upload File (1 credit)'}
      </Button>

      {isLoading && <CircularProgress size={24} />}

      {uploadStatus && (
        <Typography 
          variant="body2" 
          color={uploadStatus.includes('successfully') ? 'primary' : 
                uploadStatus.includes('Error') ? 'error' : 'text.secondary'}
          sx={{ textAlign: 'center', mt: 1 }}
        >
          {uploadStatus}
        </Typography>
      )}
    </Box>
  );
};

export default BookUploader; 